import { defineMessages } from 'react-intl';

import { BRAZIL_STATES, getStateFieldName, STATE_FIELD_NAMES } from './states';

const messages = defineMessages({
  blockTitle: {
    id: 'linkMapBlockTitle',
    defaultMessage: 'Link map',
  },
  title: {
    id: 'Title',
    defaultMessage: 'Title',
  },
  layout: {
    id: 'Layout',
    defaultMessage: 'Layout',
  },
  subtitle: {
    id: 'linkMapSubtitle',
    defaultMessage: 'Subtitle',
  },
  linkTemplate: {
    id: 'linkMapTemplateTitle',
    defaultMessage: 'State link template',
  },
  linkTemplateDescriptionIndividual: {
    id: 'linkMapLinkTemplateDescriptionIndividual',
    defaultMessage:
      'Use the link template to generate URLs automatically. The <<UF>> placeholder is replaced with the state abbreviation while preserving the placeholder casing. Examples: https://www.gov.br/news?uf=<<UF>> or /search?uf=<<uf>>. Individual links override the template.',
  },
  linksFieldsetTitle: {
    id: 'linkMapLinksFieldsetTitle',
    defaultMessage: 'State links',
  },
  linkFormat: {
    id: 'linkMapLinkFormat',
    defaultMessage: 'Link format',
  },
  linkFormatDescription: {
    id: 'linkMapLinkFormatDescription',
    defaultMessage: 'Choose how you want to configure the state links.',
  },
  linkFormatNewsSearch: {
    id: 'linkMapLinkFormatNewsSearch',
    defaultMessage: 'News search',
  },
  linkFormatIndividualLinks: {
    id: 'linkMapLinkFormatIndividualLinks',
    defaultMessage: 'Individual links',
  },
  searchAcrossPortal: {
    id: 'linkMapSearchAcrossPortal',
    defaultMessage: 'Search the entire portal',
  },
  searchAcrossPortalDescription: {
    id: 'linkMapSearchAcrossPortalDescription',
    defaultMessage:
      'Leave unchecked to limit the search to news from this site.',
  },
  linksHelpTitle: {
    id: 'linkMapLinksHelpTitle',
    defaultMessage: 'How to use state links',
  },
  linksHelpBodyIndividual: {
    id: 'linkMapLinksHelpBodyIndividual',
    defaultMessage:
      'Provide a link for each required state. These links override the template.',
  },
  useDarkTheme: {
    id: 'linkMapUseDarkTheme',
    defaultMessage: 'Use dark background',
  },
  useFullWidth: {
    id: 'linkMapUseFullWidth',
    defaultMessage: 'Use full width',
  },
});

export const linkMapSchema = ({ intl, data }) => {
  const selectedFormat = data?.linkFormat || 'news-search';

  const stateFieldsetFields = ['linkFormat'];

  if (selectedFormat === 'news-search') {
    stateFieldsetFields.push('searchAcrossPortal');
  }

  if (selectedFormat === 'individual-links') {
    stateFieldsetFields.push(
      'templateHelp',
      'linkTemplate',
      'linksHelp',
      ...STATE_FIELD_NAMES,
    );
  }

  const stateFieldset = {
    id: 'stateLinks',
    title: intl.formatMessage(messages.linksFieldsetTitle),
    fields: stateFieldsetFields,
  };

  const stateProperties = BRAZIL_STATES.reduce((acc, state) => {
    const fieldName = getStateFieldName(state.code);

    acc[fieldName] = {
      title: `${state.name} (${state.code})`,
      widget: 'url',
    };

    return acc;
  }, {});

  return {
    title: intl.formatMessage(messages.blockTitle),
    fieldsets: [
      {
        id: 'default',
        title: intl.formatMessage(messages.blockTitle),
        fields: ['title', 'subtitle'],
      },
      {
        id: 'layout',
        title: intl.formatMessage(messages.layout),
        fields: ['useFullWidth', 'useDarkTheme'],
      },
      stateFieldset,
    ],
    properties: {
      title: {
        title: intl.formatMessage(messages.title),
        widget: 'text',
      },
      subtitle: {
        title: intl.formatMessage(messages.subtitle),
        widget: 'text',
      },
      linkFormat: {
        title: intl.formatMessage(messages.linkFormat),
        description: intl.formatMessage(messages.linkFormatDescription),
        widget: 'radio_group',
        choices: [
          ['news-search', intl.formatMessage(messages.linkFormatNewsSearch)],
          [
            'individual-links',
            intl.formatMessage(messages.linkFormatIndividualLinks),
          ],
        ],
        default: 'news-search',
      },
      searchAcrossPortal: {
        title: intl.formatMessage(messages.searchAcrossPortal),
        description: intl.formatMessage(messages.searchAcrossPortalDescription),
        type: 'boolean',
        default: false,
      },
      useFullWidth: {
        title: intl.formatMessage(messages.useFullWidth),
        type: 'boolean',
        default: true,
      },
      linkTemplate: {
        title: intl.formatMessage(messages.linkTemplate),
        widget: 'text',
      },
      useDarkTheme: {
        title: intl.formatMessage(messages.useDarkTheme),
        type: 'boolean',
        defaultValue: false,
      },
      templateHelp: {
        widget: 'help_message_widget',
        body: intl.formatMessage(messages.linkTemplateDescriptionIndividual),
      },
      linksHelp: {
        widget: 'help_message_widget',
        body: intl.formatMessage(messages.linksHelpBodyIndividual),
      },

      ...stateProperties,
    },
    required: ['title', 'linkFormat'],
  };
};

export default linkMapSchema;
